import AuditInfo from 'components/audit/AuditInfo';
import { CircleAlert } from 'components/icons';
import { useIsSectionEditing, useOpenSection } from 'components/section-edit/SectionEdit';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import InvoiceLoader from 'pages/invoices/layout/views/components/InvoiceLoader';
import ExistingPaymentsCard from 'pages/invoices/layout/views/general-tab/existing-payments/ExistingPaymentsCard';
import KsefDetailsCard from 'pages/invoices/layout/views/general-tab/KsefDetailsCard';
import PrepaymentUsageCard from 'pages/invoices/layout/views/general-tab/PrepaymentUsageCard';
import RevenueTeamCard from 'pages/invoices/layout/views/general-tab/RevenueTeamCard';
import {
    useShowsExistingPayments,
    useShowsKsefDetails,
} from 'pages/invoices/layout/views/general-tab/useGeneralCardRules';
import { FormattedMessage } from 'react-intl';
import { useHashScroll } from 'utils/useHashScroll';
import { AlertActionButton, DetailsAlert } from '../views/details-tab/Details';
import { ReadCard } from './general/GeneralCard';
import { DebtCollectionSection, InvoiceDetailsSection, RecipientsSection } from './general/GeneralFormSections';
import { IssuedForCasesCard } from './general/IssuedForCasesCard';
import { PayerSection } from './general/PayerSection';
import { PreviousInvoicesSection } from './general/PreviousInvoicesSection';
import { SettingsSection } from './general/SettingsSection';
import { InvoiceTabProps } from './invoiceTabProps';

/**
 * The General tab of the new design: classic's cards in their order, Details split into Settings, Payer and Issued
 * for cases. A card with a form reads until Edit and edits in place; the others read as they are. A missing Signed by
 * leads, with a way into Settings' edit. Of the flow's notices only the agreed postponement is this tab's: the band
 * draws the others.
 */
export function GeneralTab(props: InvoiceTabProps) {
    const {
        invoice,
        classifiers,
        isInViewMode,
        handleStatusLoadingState,
        isLoading,
        searchExistingPrepaymentsText,
        setSearchExistingPrepaymentsText,
        existingPrepayments,
        alerts,
    } = props;
    const { isCredit, isPrepayment } = useInvoice(invoice);
    const showsExistingPayments = useShowsExistingPayments(invoice);
    const showsKsefDetails = useShowsKsefDetails(invoice);
    useHashScroll();
    const openSection = useOpenSection();
    // Opening it while another card edits would drop that card's changes: its action waits as the cards' Edits do.
    const isSectionEditing = useIsSectionEditing();

    const lastTransition = invoice.invoiceTransitions[0];
    const showsPostponeConfirmed = !!lastTransition && alerts.some((alert) => alert.id === 'postpone-confirmed');

    if (isLoading) {
        return (
            <ReadCard title={<FormattedMessage id="invoices.general-tab.details.title" />}>
                <InvoiceLoader />
            </ReadCard>
        );
    }

    return (
        <>
            {alerts.some((alert) => alert.id === 'signed-by-missing') ? (
                <DetailsAlert
                    tone="warning"
                    action={
                        openSection && !isSectionEditing ? (
                            <AlertActionButton onClick={() => openSection('settings')}>
                                <FormattedMessage id="invoices.general-tab.details.edit-settings" />
                            </AlertActionButton>
                        ) : undefined
                    }
                >
                    <FormattedMessage id="invoices.tooltips.signed-by-missing" />
                </DetailsAlert>
            ) : null}
            {showsPostponeConfirmed ? (
                <Alert role="note" className="border-warning-border bg-warning-tint text-warning-ink">
                    <CircleAlert aria-hidden />
                    <AlertTitle>
                        <FormattedMessage
                            id="invoices.alerts.confirmed-postpone-name"
                            values={{ name: lastTransition.changedByEmployee?.name || '-' }}
                        />
                    </AlertTitle>
                    {lastTransition.comment || lastTransition.matter ? (
                        <AlertDescription className="text-current">
                            {lastTransition.comment ? (
                                <div>
                                    <FormattedMessage
                                        id="invoices.alerts.reason"
                                        values={{ reason: lastTransition.comment }}
                                    />
                                </div>
                            ) : null}
                            {lastTransition.matter ? (
                                <div>
                                    <FormattedMessage
                                        id="invoices.alerts.case"
                                        values={{ case: lastTransition.matter.name }}
                                    />
                                </div>
                            ) : null}
                        </AlertDescription>
                    ) : null}
                </Alert>
            ) : null}
            <SettingsSection {...props} />
            <PayerSection {...props} />
            <IssuedForCasesCard invoice={invoice} />
            {showsKsefDetails ? (
                <ReadCard title={<FormattedMessage id="invoices.general-tab.ksef-details.title" />}>
                    <KsefDetailsCard
                        invoiceExternalEvents={invoice.invoiceExternalEvents ?? []}
                        externalStatusTypeCode={invoice.externalStatusTypeCode}
                        externalPayload={invoice.externalPayload}
                        isInCard
                    />
                </ReadCard>
            ) : null}
            <PreviousInvoicesSection invoice={invoice} classifiers={classifiers} />
            <RecipientsSection {...props} />
            {showsExistingPayments ? (
                <ReadCard title={<FormattedMessage id="invoices.general-tab.existing-payments.title" />}>
                    <ExistingPaymentsCard
                        invoice={invoice}
                        isInViewMode={isInViewMode || invoice.hasCreditInvoice}
                        searchText={searchExistingPrepaymentsText}
                        setSearchText={setSearchExistingPrepaymentsText}
                        existingPrepayments={existingPrepayments}
                    />
                </ReadCard>
            ) : null}
            <InvoiceDetailsSection {...props} />
            <DebtCollectionSection invoice={invoice} />
            {!isCredit || !!invoice.invoicePayments?.length ? (
                <RevenueTeamCard
                    invoice={invoice}
                    classifiers={classifiers}
                    isInViewMode={isInViewMode}
                    handleStatusLoadingState={handleStatusLoadingState}
                    isInCard
                />
            ) : null}
            {isPrepayment ? (
                <ReadCard title={<FormattedMessage id="invoices.general-tab.prepayment-usage.title" />}>
                    <PrepaymentUsageCard invoice={invoice} isInCard />
                </ReadCard>
            ) : null}
            <AuditInfo
                createdAt={invoice.createdAt}
                createdByEmployee={invoice.createdByEmployee || undefined}
                lastModifiedAt={invoice.lastModifiedAt}
                changedByEmployee={invoice.changedByEmployee || undefined}
            />
        </>
    );
}
